import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Ticket } from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../lib/seo";
import { Reveal } from "../lib/motion";
import { speLink, trackOutbound } from "../lib/track";

export default function Experiences() {
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    api.get("/experiences").then((r) => setExperiences(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="experiences-page">
      <Seo
        title="The Experiences | Included in Every Perfect Break | The Perfect Break"
        description="Every Perfect Break includes three experiences you choose: heritage sites, distilleries, river boating, galleries, gardens and food trails across the Heart of England."
        path="/experiences"
      />
      <section className="bg-primary py-20 text-sand sm:py-28">
        <div className="container-tpb">
          <p className="overline !text-sand/80">The difference</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-medium tracking-tight sm:text-5xl" data-testid="experiences-heading">
            A hotel room is just a room. These are the stories.
          </h1>
          <p className="mt-5 max-w-xl leading-relaxed text-sand/80" data-testid="experiences-intro">
            Every Perfect Break includes your choice of three experiences from a hand-picked local collection. Tap any experience to read the exact offer on our booking site.
          </p>
          <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-sand/30 px-5 py-2.5 text-sm font-semibold" data-testid="experiences-included-badge">
            <Ticket className="h-4 w-4" /> Three included in every break
          </p>
        </div>
      </section>

      <section className="container-tpb py-16 sm:py-20">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6" data-testid="experiences-grid">
          {experiences.map((e, i) => (
            <a
              key={e.name}
              href={speLink(e.spe_url, `experience-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutbound({ break_slug: null, destination_url: e.spe_url, placement: "experience-tile" })}
              className="card-flat group block overflow-hidden"
              data-testid={`experience-card-${i}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={e.image} alt={e.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]" />
              </div>
              <div className="p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent">{e.type} · {e.destination_name}</p>
                <h2 className="mt-2 font-serif text-xl font-semibold leading-tight text-ink transition-colors duration-300 group-hover:text-primary">{e.name}</h2>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{e.blurb}</p>
                <p className="link-sweep mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-primary" data-testid={`experience-details-link-${i}`}>
                  See the exact offer <ArrowUpRight className="h-3.5 w-3.5" />
                </p>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-16 rounded-lg bg-sand-dark p-8 text-center sm:p-12" data-testid="experiences-cta">
          <h2 className="mx-auto max-w-xl font-serif text-3xl font-medium text-primary">Which three will you choose?</h2>
          <p className="mx-auto mt-3 max-w-md text-ink-muted">Pick a break, then choose your three experiences when you book. All included, from £169 for two.</p>
          <Link to="/breaks" className="btn-accent group mt-7" data-testid="experiences-find-break-button">
            Find your perfect break <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </main>
  );
}
